// AdminLayout — port of cardrive/admin-shell.jsx::AdminShell.
// Sidebar + collapsible state + mobile drawer + topbar + role switcher.
// Route navigation is via TanStack Router's <Link>; the active module is
// derived from the current path.

import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { AdminIcon } from '@drivecars/admin-ui';
import { BrandGlyph, BrandMark } from '@/components/Brand';
import { Avatar } from '@drivecars/admin-ui';
import { CommandPalette } from '@/components/CommandPalette';
import { fetchAdminAttention } from '@/lib/admin-api';
import { useAuth } from '@/lib/auth';
import { useRole } from '@/lib/role';
import {
  ADMIN_MODULES,
  ROLE_PERMS,
  TRANSFER_MODULES,
  type AdminModule,
  type ModuleGroup,
  type RoleId,
} from '@/data/mock';
import { TRANSFERS_UI_ENABLED } from '@/lib/features';

const GROUPS: Array<{ id: ModuleGroup; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'operate', label: 'Operate' },
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'trust', label: 'Trust' },
  { id: 'commerce', label: 'Commerce' },
  { id: 'finance', label: 'Finance' },
  { id: 'system', label: 'System' },
];

function pathToModuleId(pathname: string): string | null {
  // /dashboard -> dashboard, /bookings -> bookings, /bookings/BK-1 -> bookings
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] ?? null;
}

function moduleVisible(m: AdminModule, role: RoleId): boolean {
  if (!TRANSFERS_UI_ENABLED && TRANSFER_MODULES.has(m.id)) return false;
  const perms = ROLE_PERMS[role];
  if (perms.modules === 'all') return true;
  return perms.modules.includes(m.id);
}

export interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const { role, setRole, canSwitch } = useRole();

  // Global Cmd/Ctrl-K shortcut. Document-level so it fires regardless of
  // focus, including inside text inputs (consistent with the convention
  // most editors follow).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Sidebar attention counts — polled every 60s. Cheap COUNT(*) per
  // category (see apps/api/.../attention.ts). When a tab is hidden the
  // tanstack-query default backs off, so this isn't a wasted heartbeat.
  const attentionQ = useQuery({
    queryKey: ['admin', 'attention'],
    queryFn: ({ signal }) => fetchAdminAttention(signal),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const attentionCount = (id: string): number | null => {
    const a = attentionQ.data;
    if (!a) return null;
    switch (id) {
      case 'bookings':
        return a.bookings;
      case 'cars':
        return a.cars;
      case 'crew':
        return a.vendors;
      case 'reviews':
        return a.reviews;
      case 'inquiries':
        return a.inquiries;
      case 'support':
        return a.support;
      case 'payouts':
        return a.payouts;
      default:
        return null;
    }
  };
  // Notification bell items — areas with a non-zero attention count, linked to
  // their queue. Drives the bell dropdown + the red dot.
  const attentionItems = (
    [
      { label: 'Bookings', path: '/bookings', count: attentionQ.data?.bookings ?? 0 },
      { label: 'Cars', path: '/cars', count: attentionQ.data?.cars ?? 0 },
      { label: 'Crew', path: '/crew', count: attentionQ.data?.vendors ?? 0 },
      { label: 'Reviews', path: '/reviews', count: attentionQ.data?.reviews ?? 0 },
      { label: 'Inquiries', path: '/inquiries', count: attentionQ.data?.inquiries ?? 0 },
      { label: 'Support', path: '/support', count: attentionQ.data?.support ?? 0 },
      { label: 'Payouts', path: '/payouts', count: attentionQ.data?.payouts ?? 0 },
    ] as const
  ).filter((i) => i.count > 0);
  const attentionTotal = attentionItems.reduce((sum, i) => sum + i.count, 0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const active = pathToModuleId(location.pathname);

  const userName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email
    : 'You';

  async function onLogout() {
    await logout();
    await navigate({ to: '/login' as never, replace: true });
  }

  // Lock body scroll when mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const perms = ROLE_PERMS[role];

  return (
    <div className="min-h-screen bg-[color:var(--bo-bg)] text-[color:var(--bo-ink)]">
      {/* Mobile backdrop */}
      {mobileOpen ? (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
        />
      ) : null}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-[color:var(--bo-line)] bg-white transition-transform lg:translate-x-0 lg:transition-all ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${collapsed ? 'lg:w-[64px]' : 'lg:w-[244px]'} w-[260px]`}
      >
        <div className="flex h-14 items-center justify-between border-b border-[color:var(--bo-line)] px-4">
          {!collapsed ? (
            <Link to={'/dashboard' as never}>
              <BrandMark />
            </Link>
          ) : (
            <Link to={'/dashboard' as never} className="grid h-7 w-7 place-items-center">
              <BrandGlyph />
            </Link>
          )}
          {/* Desktop collapse */}
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className="hidden text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)] lg:inline-flex"
            aria-label="Toggle sidebar"
          >
            <AdminIcon name="chev" className={`h-4 w-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
          </button>
          {/* Mobile close */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)] lg:hidden"
            aria-label="Close menu"
          >
            <AdminIcon name="x" className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {GROUPS.map((g) => {
            const items = ADMIN_MODULES.filter(
              (m) => m.group === g.id && moduleVisible(m, role),
            );
            if (items.length === 0) return null;
            return (
              <div key={g.id} className="mb-3">
                {!collapsed ? (
                  <div className="px-4 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
                    {g.label}
                  </div>
                ) : null}
                {items.map((m) => {
                  const isActive = active === m.id;
                  const count = attentionCount(m.id);
                  // Hide zero badges — "Reviews 0" is visual noise. Show
                  // the badge only when there's actually something pending.
                  const showBadge = count != null && count > 0;
                  return (
                    <Link
                      key={m.id}
                      to={`/${m.id}` as never}
                      onClick={() => setMobileOpen(false)}
                      className={`mx-2 flex items-center gap-3 rounded-lg px-2.5 py-2 text-[13px] font-medium ${
                        isActive
                          ? 'bg-[color:var(--bo-bg)] text-[color:var(--bo-ink)]'
                          : 'text-[color:var(--bo-ink)]/75 hover:bg-[color:var(--bo-bg)] hover:text-[color:var(--bo-ink)]'
                      }`}
                      title={collapsed ? m.label : undefined}
                    >
                      <AdminIcon name={m.icon as Parameters<typeof AdminIcon>[0]['name']} className="h-4 w-4 shrink-0" />
                      <span className={`flex-1 truncate ${collapsed ? 'lg:hidden' : ''}`}>
                        {m.label}
                      </span>
                      {showBadge ? (
                        <span
                          className={`rounded-md bg-[#fef2f2] px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums text-[#9f1239] ${
                            collapsed ? 'lg:hidden' : ''
                          }`}
                          aria-label={`${count} items need attention`}
                        >
                          {count.toLocaleString()}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Role switcher — super_admin only (preview as another role for
            QA/debug). Non-super-admins see a static label showing their
            scoped role. Collapsed sidebar hides both. */}
        {!collapsed ? (
          <div className="border-t border-[color:var(--bo-line)] p-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
              {canSwitch ? 'Viewing as' : 'Your role'}
            </div>
            {canSwitch ? (
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as RoleId)}
                className="mt-1.5 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-2.5 py-2 text-[12.5px] font-medium outline-none focus:border-[color:var(--bo-accent)]"
              >
                {Object.entries(ROLE_PERMS).map(([id, r]) => (
                  <option key={id} value={id}>
                    {r.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="mt-1.5 rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] px-2.5 py-2 text-[12.5px] font-medium">
                {perms.label}
              </div>
            )}
            <div className="mt-2 text-[10.5px] text-[color:var(--bo-mute)]">
              {perms.readonly ? 'Read-only access' : 'Full module access'}
            </div>
          </div>
        ) : null}
      </aside>

      {/* Main */}
      <div className={`transition-all ${collapsed ? 'lg:pl-[64px]' : 'lg:pl-[244px]'}`}>
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-[color:var(--bo-line)] bg-white/95 px-3 backdrop-blur sm:px-4 lg:px-6">
          {/* min-w-0 so the search child can actually shrink: a flex item
              defaults to min-width:auto and would otherwise refuse to go below
              its content width, pushing the header actions off-screen. */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="grid h-9 w-9 place-items-center rounded-lg border border-[color:var(--bo-line)] hover:border-[color:var(--bo-mute)] lg:hidden"
              aria-label="Open menu"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
            <Link to={'/dashboard' as never} className="flex items-center gap-1.5 lg:hidden">
              <BrandGlyph />
              <span className="text-[13px] font-semibold tracking-tight">Admin</span>
            </Link>
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              // Grows into the header instead of sitting at a fixed 260/420px.
              // Fixed widths left a ~560px void between the search and the
              // right-hand actions, which read as broken alignment rather than
              // as deliberate space. Capped so it stops short of the actions on
              // wide screens.
              className="relative hidden h-9 w-full max-w-[520px] items-center gap-2 rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] pl-3 pr-2 text-[13px] text-[color:var(--bo-mute)] transition hover:border-[color:var(--bo-mute)] focus:border-[color:var(--bo-accent)] focus:bg-white focus:outline-none md:inline-flex"
              aria-label="Open quick search (Cmd-K)"
            >
              <AdminIcon name="search" className="h-3.5 w-3.5" />
              <span className="flex-1 truncate text-start">
                Search bookings, cars, customers…
              </span>
              <span className="rounded-md border border-[color:var(--bo-line)] bg-white px-1.5 py-0.5 font-mono text-[10.5px] text-[color:var(--bo-mute)]">
                ⌘K
              </span>
            </button>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <a
              href="https://sandbox.drivecars.ai"
              target="_blank"
              rel="noreferrer"
              className="hidden rounded-lg border border-[color:var(--bo-line)] px-3 py-1.5 text-[12px] font-medium text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)] md:inline-flex"
            >
              View site ↗
            </a>
            <a
              href="https://cms.sandbox.drivecars.ai/admin"
              target="_blank"
              rel="noreferrer"
              className="hidden rounded-lg border border-[color:var(--bo-line)] px-3 py-1.5 text-[12px] font-medium text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)] md:inline-flex"
            >
              CMS ↗
            </a>
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="grid h-9 w-9 place-items-center rounded-lg border border-[color:var(--bo-line)] hover:border-[color:var(--bo-mute)] md:hidden"
              aria-label="Search"
            >
              <AdminIcon name="search" className="h-4 w-4" />
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => setBellOpen((o) => !o)}
                className="relative grid h-9 w-9 place-items-center rounded-lg border border-[color:var(--bo-line)] hover:border-[color:var(--bo-mute)]"
                aria-label={
                  attentionTotal > 0
                    ? `Notifications, ${attentionTotal} item${attentionTotal === 1 ? '' : 's'} need attention`
                    : 'Notifications'
                }
                aria-expanded={bellOpen}
              >
                <AdminIcon name="bell" className="h-4 w-4" />
                {attentionTotal > 0 ? (
                  <span className="absolute end-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-[#e11d48]" />
                ) : null}
              </button>
              {bellOpen ? (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
                  <div className="absolute end-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border border-[color:var(--bo-line)] bg-white p-1.5 shadow-xl">
                    <div className="px-2 py-1.5 text-[12px] font-semibold text-[color:var(--bo-ink)]">
                      Needs attention
                    </div>
                    {attentionItems.length === 0 ? (
                      <div className="px-2 py-3 text-[12.5px] text-[color:var(--bo-mute)]">
                        {attentionQ.isLoading ? 'Loading…' : "You're all caught up."}
                      </div>
                    ) : (
                      attentionItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path as never}
                          onClick={() => setBellOpen(false)}
                          className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-[13px] text-[color:var(--bo-ink)] hover:bg-[color:var(--bo-line)]/40"
                        >
                          <span>{item.label}</span>
                          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#e11d48] px-1.5 text-[11px] font-semibold tabular-nums text-white">
                            {item.count}
                          </span>
                        </Link>
                      ))
                    )}
                  </div>
                </>
              ) : null}
            </div>
            {user ? (
              <div className="flex items-center gap-2">
                <Avatar name={userName} size={32} />
                <button
                  type="button"
                  onClick={() => void onLogout()}
                  className="hidden rounded-lg border border-[color:var(--bo-line)] px-3 py-1.5 text-[12px] font-medium text-[color:var(--bo-mute)] hover:text-[color:var(--bo-ink)] sm:inline-flex"
                  title={`Sign out ${user.email}`}
                >
                  Sign out
                </button>
              </div>
            ) : (
              <Avatar name={userName} size={32} />
            )}
          </div>
        </header>

        {/* Content */}
        <main className="px-3 py-4 sm:px-4 sm:py-5 lg:px-6 lg:py-6">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
